import React, { useEffect, useState } from "react";
import { api, BRAND, formatApiError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Megaphone } from "lucide-react";
import { useConfirm } from "@/components/ConfirmDialog";

export default function Announcements() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "" });
  const confirm = useConfirm();

  const load = async () => {
    try { const { data } = await api.get("/announcements"); setItems(data); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/announcements", form);
      toast.success("Aviso publicado");
      setForm({ title: "", description: "" });
      setOpen(false);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const del = async (id) => {
    const ok = await confirm({ title: "Excluir aviso?", confirmText: "Excluir", destructive: true });
    if (!ok) return;
    await api.delete(`/announcements/${id}`);
    load();
  };

  return (
    <div data-testid="admin-announcements-page">
      <PageHeader
        eyebrow="Comunicação"
        title="Mural de avisos"
        subtitle="Publique novidades para todos os alunos."
        mascot={BRAND.MANGUITA_FORCA}
        actions={
          <Button onClick={() => setOpen(true)} className="bg-[hsl(var(--vv-lime))] text-[hsl(218_50%_10%)] font-bold uppercase" data-testid="new-announcement-button">
            <Plus className="size-4 mr-2" /> Novo aviso
          </Button>
        }
      />

      {items.length === 0 ? (
        <EmptyState title="Nenhum aviso" description="Publique o primeiro aviso para sua comunidade." mascot={BRAND.MANGUITO} />
      ) : (
        <div className="space-y-3">
          {items.map((a) => (
            <div key={a.id} className="vv-card p-5" data-testid={`announcement-${a.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <div className="size-10 rounded-lg bg-primary/15 flex items-center justify-center">
                    <Megaphone className="size-5 text-[hsl(var(--vv-yellow))]" />
                  </div>
                  <div>
                    <div className="font-black text-lg">{a.title}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {new Date(a.created_at).toLocaleString("pt-BR")}
                    </div>
                    <p className="mt-2 text-sm whitespace-pre-wrap">{a.description}</p>
                  </div>
                </div>
                <Button size="icon" variant="ghost" className="text-destructive" onClick={() => del(a.id)}>
                  <Trash2 className="size-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="announcement-dialog">
          <DialogHeader>
            <DialogTitle>Novo aviso</DialogTitle>
            <DialogDescription>Publique uma mensagem para todos os alunos da escola.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div>
              <Label>Título *</Label>
              <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="announcement-title-input" />
            </div>
            <div>
              <Label>Descrição *</Label>
              <Textarea required rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="announcement-desc-input" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button type="submit" data-testid="save-announcement-button">Publicar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
